import React, { useEffect, useMemo, useState } from "react";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  InfoWindow,
} from "react-google-maps";
import { Box, Stack, Typography, Button, Skeleton } from "@mui/material";
import { MdPhone, MdEmail, MdLocationOn, MdOpenInNew } from "react-icons/md";

import { StatusChip } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "./api";

/**
 * The pharmacy map.
 *
 * Fed by its own endpoint rather than by the table's rows: the table is
 * server-paged, so drawing it would show whichever twenty-five pharmacies
 * were on page one and quietly claim that was the estate. This asks for every
 * pin matching the current filter, and says so when the server caps it.
 *
 * Pins are coloured by what the pharmacy is actually doing, because "we have
 * 300 pharmacies" and "300 pharmacies are taking orders right now" are very
 * different facts and a map of identical markers cannot tell them apart:
 *
 *   live       verified AND taking orders — a customer can be routed here now
 *   paused     verified but not taking orders — approved, currently closed
 *   registered signed up, not yet approved — no orders reach it
 *
 * The legend is the control as well as the key: clicking a state hides it, so
 * "show me only what is registered but never went live" is one click.
 */
const MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  "AIzaSyBjgK-bezlAjIF6PUj_239Ln9jYSaO_A30";

const MAP_URL = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=geometry,drawing,places`;

const HYDERABAD = { lat: 17.4374837, lng: 78.5158477 };

const STATES = {
  live: { label: "Live", color: T.success, hint: "verified · taking orders" },
  paused: { label: "Approved, paused", color: T.secondary, hint: "verified · closed" },
  registered: {
    label: "Registered only",
    color: T.warning,
    hint: "awaiting approval",
  },
};
const STATE_ORDER = ["live", "paused", "registered"];

const stateOf = (pin) => {
  if (!pin.verifiedPharamacy) return "registered";
  return pin.isTakingOrders ? "live" : "paused";
};

// Muted base map, so the pins are the only saturated thing on it.
const MAP_STYLES = [
  { featureType: "all", elementType: "geometry", stylers: [{ color: "#f4f6f6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dce8e8" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
];

/**
 * A drawn circle rather than an image marker: the previous map used one
 * bitmap for every pin, which made a live pharmacy and a never-approved one
 * look identical. A symbol can carry the state in its fill.
 */
const symbol = (state, active) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: active ? 11 : state === "live" ? 8 : 7,
  fillColor: STATES[state].color,
  fillOpacity: state === "registered" ? 0.85 : 1,
  strokeColor: "#ffffff",
  strokeWeight: active ? 3 : 2,
});

const Canvas = withScriptjs(
  withGoogleMap(({ pins, active, onActivate, onClose, onOpen, center, zoom }) => (
    <GoogleMap
      defaultZoom={zoom}
      defaultCenter={center}
      options={{
        styles: MAP_STYLES,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        zoomControl: true,
      }}
    >
      {pins.map((pin) => (
        <Marker
          key={pin.pharmacyId}
          position={pin.coordinates}
          title={pin.bussinessName || undefined}
          // The selected pin is drawn last so it sits above its neighbours.
          zIndex={active?.pharmacyId === pin.pharmacyId ? 999 : undefined}
          icon={symbol(stateOf(pin), active?.pharmacyId === pin.pharmacyId)}
          onClick={() => onActivate(pin.pharmacyId)}
        />
      ))}

      {active && (
        <InfoWindow position={active.coordinates} onCloseClick={onClose}>
          <Box sx={{ minWidth: 215, maxWidth: 285, p: 0.5 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: T.primary }}>
              {active.bussinessName || "Unnamed pharmacy"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.muted, mb: 0.8 }}>
              {active.fullName ? `Owner: ${active.fullName}` : active.medstownid}
            </Typography>

            <Stack spacing={0.5}>
              {active.businessPhone && (
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <MdPhone size={13} color={T.secondary} />
                  <Typography sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {active.businessPhone}
                  </Typography>
                </Stack>
              )}
              {active.email && (
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <MdEmail size={13} color={T.subtle} />
                  <Typography sx={{ fontSize: "0.72rem", wordBreak: "break-all" }}>
                    {active.email}
                  </Typography>
                </Stack>
              )}
              {active.address && (
                <Stack direction="row" spacing={0.7} alignItems="flex-start">
                  <MdLocationOn size={13} color={T.secondary} style={{ marginTop: 2 }} />
                  <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>
                    {active.address}
                    {active.pincode ? ` · ${active.pincode}` : ""}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <StatusChip
                status={active.verifiedPharamacy ? "verified" : "pending"}
                label={active.verifiedPharamacy ? "Verified" : "Registered"}
              />
              <StatusChip
                status={active.isTakingOrders ? "accepted" : "pending"}
                label={active.isTakingOrders ? "Taking orders" : "Paused"}
              />
            </Stack>

            <Button
              size="small"
              fullWidth
              startIcon={<MdOpenInNew size={14} />}
              onClick={() => onOpen(active.pharmacyId)}
              sx={{ mt: 1, textTransform: "none", color: T.primary, fontSize: "0.75rem" }}
            >
              Open console
            </Button>
          </Box>
        </InfoWindow>
      )}
    </GoogleMap>
  ))
);

/** Shell for every non-map state, so the panel never changes size or shape. */
function Panel({ height, children }) {
  return (
    <Box
      sx={{
        height,
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        px: 3,
        borderRadius: 3,
        border: `1px solid ${T.border}`,
        bgcolor: T.surface,
      }}
    >
      {children}
    </Box>
  );
}

export default function PharmacyMap({ filters, height = 420, onOpenConsole }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [hidden, setHidden] = useState([]);

  // Serialised, so the effect re-runs when a filter value changes rather than
  // on every render that rebuilds the filters object.
  const key = JSON.stringify(filters);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    api
      .listMapPins(JSON.parse(key))
      .then((data) => {
        if (cancelled) return;
        setPins((data?.pharmacies || []).filter((p) => p.coordinates));
        setTruncated(Boolean(data?.truncated));
        setActiveId(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message || err?.message || "Could not load the map."
        );
        setPins([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [key]);

  const counts = useMemo(() => {
    const tally = { live: 0, paused: 0, registered: 0 };
    pins.forEach((pin) => {
      tally[stateOf(pin)] += 1;
    });
    return tally;
  }, [pins]);

  const visible = useMemo(
    () => pins.filter((pin) => !hidden.includes(stateOf(pin))),
    [pins, hidden]
  );

  const active = useMemo(
    () => visible.find((p) => p.pharmacyId === activeId) || null,
    [visible, activeId]
  );

  // Centre on the pins rather than always on Hyderabad, so a filter that
  // lands on one city does not open half a country away.
  const center = useMemo(() => {
    if (!pins.length) return HYDERABAD;
    const sum = pins.reduce(
      (acc, p) => ({
        lat: acc.lat + p.coordinates.lat,
        lng: acc.lng + p.coordinates.lng,
      }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / pins.length, lng: sum.lng / pins.length };
  }, [pins]);

  const toggleState = (state) =>
    setHidden((list) =>
      list.includes(state) ? list.filter((s) => s !== state) : [...list, state]
    );

  if (loading) {
    return <Skeleton variant="rounded" height={height} sx={{ borderRadius: 3 }} />;
  }

  if (error) {
    return (
      <Panel height={height}>
        <Typography sx={{ color: T.error, fontWeight: 600 }}>{error}</Typography>
      </Panel>
    );
  }

  if (!pins.length) {
    return (
      <Panel height={height}>
        <Box>
          <Typography sx={{ color: T.muted, fontWeight: 600 }}>
            No mapped pharmacies match these filters.
          </Typography>
          <Typography sx={{ color: T.subtle, fontSize: "0.8rem", mt: 0.5 }}>
            A pharmacy only appears here once someone has set its location — filter
            by “On the map: No” to see which ones are missing one.
          </Typography>
        </Box>
      </Panel>
    );
  }

  return (
    <Box>
      {/* ── Legend, which is also the map's filter ─────────────────────── */}
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1 }}
      >
        {STATE_ORDER.map((state) => {
          const off = hidden.includes(state);
          const { label, color, hint } = STATES[state];
          return (
            <Box
              key={state}
              onClick={() => toggleState(state)}
              title={`${hint} — click to ${off ? "show" : "hide"}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                px: 1.2,
                py: 0.6,
                borderRadius: 2,
                cursor: "pointer",
                userSelect: "none",
                border: `1px solid ${off ? T.border : tint(color, 0.45)}`,
                bgcolor: off ? "transparent" : tint(color, 0.08),
                opacity: off ? 0.5 : 1,
                transition: "all 0.15s ease",
                "&:hover": { borderColor: color },
              }}
            >
              <Box
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  bgcolor: off ? T.subtle : color,
                  border: "2px solid #fff",
                  boxShadow: `0 0 0 1px ${off ? T.border : color}`,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  color: off ? T.subtle : T.ink,
                  textDecoration: off ? "line-through" : "none",
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  fontWeight: 700,
                  color: off ? T.subtle : color,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {counts[state]}
              </Typography>
            </Box>
          );
        })}

        <Typography sx={{ fontSize: "0.72rem", color: T.subtle, ml: "auto" }}>
          {visible.length} of {pins.length} pins shown
          {truncated && (
            <Box component="span" sx={{ color: T.warning, fontWeight: 600 }}>
              {" "}
              · capped by the server, narrow the filters to see the rest
            </Box>
          )}
        </Typography>
      </Stack>

      <Box
        sx={{
          height,
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${T.border}`,
        }}
      >
        <Canvas
          googleMapURL={MAP_URL}
          loadingElement={<div style={{ height: "100%" }} />}
          containerElement={<div style={{ height: "100%", width: "100%" }} />}
          mapElement={<div style={{ height: "100%" }} />}
          pins={visible}
          active={active}
          center={center}
          zoom={pins.length === 1 ? 14 : 11}
          onActivate={setActiveId}
          onClose={() => setActiveId(null)}
          onOpen={onOpenConsole}
        />
      </Box>
    </Box>
  );
}
